import { ALLOWED_COMPANY_LOGO_CONTENT_TYPES } from '@azymmetric/shared/attachments/attachment.constants';
import { CompanyLogoPreview } from '@components/shared/company-logo-preview.component';
import { FormLabel as FormLabelComponent } from '@components/shared/form-label.component';
import { Button } from '@components/ui/button';
import { cn } from '@lib/utils';
import { EditIcon, UploadIcon } from 'lucide-react';
import { type ChangeEvent, type DragEvent, type KeyboardEvent, useRef, useState } from 'react';
import type { CompanyProfileModalDetailsAdditionalProps } from './company-company-profile-modal-details.types';

const COMPANY_LOGO_FORMAT_LABELS_BY_MIME: Record<string, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/svg+xml': 'SVG',
  'image/webp': 'WEBP',
};

const COMPANY_LOGO_TOOLTIP_FORMATS = ALLOWED_COMPANY_LOGO_CONTENT_TYPES.map(
  (mimeType) => COMPANY_LOGO_FORMAT_LABELS_BY_MIME[mimeType] ?? mimeType,
).join(', ');

export const CompanyLogoField = (props: CompanyProfileModalDetailsAdditionalProps) => {
  const {
    companyLogoError,
    companyLogoPreviewUrl,
    isCompanyLogoDeleteRequested,
    onCompanyLogoFileSelect,
    onCompanyLogoRemove,
    onCompanyLogoRestore,
  } = props;
  const [isDragOver, setIsDragOver] = useState(false);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const openFilePicker = () => {
    logoFileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    onCompanyLogoFileSelect?.(selectedFile);
    event.target.value = '';
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const selectedFile = event.dataTransfer.files?.[0];
    onCompanyLogoFileSelect?.(selectedFile);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFilePicker();
    }
  };

  const showRestoreAction = Boolean(isCompanyLogoDeleteRequested);
  const showRemoveAction = Boolean(companyLogoPreviewUrl);
  const canClickPreviewArea = !companyLogoPreviewUrl;

  return (
    <div className="flex w-full max-w-40 shrink-0 flex-col gap-2">
      <FormLabelComponent
        label="Logo"
        tooltip={`Recommended: square logo up to 512 x 512 px. Formats: ${COMPANY_LOGO_TOOLTIP_FORMATS}.`}
      />
      <div
        className={cn(
          'group relative flex size-40 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 transition-colors',
          canClickPreviewArea ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
          isDragOver && 'border-blue-400 bg-blue-50',
        )}
        aria-label={canClickPreviewArea ? 'Add organization logo' : undefined}
        role={canClickPreviewArea ? 'button' : undefined}
        tabIndex={canClickPreviewArea ? 0 : undefined}
        onClick={canClickPreviewArea ? openFilePicker : undefined}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onKeyDown={canClickPreviewArea ? handleKeyDown : undefined}
      >
        {companyLogoPreviewUrl ? (
          <CompanyLogoPreview
            className="h-full w-full rounded-xl bg-transparent"
            fallback={null}
            logoAlt="Organization logo preview"
            logoClassName="rounded-2xl"
            logoUrl={companyLogoPreviewUrl}
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-gray-500">
            <UploadIcon className="size-5" />
            <span className="text-xs font-medium">Add</span>
          </div>
        )}
        {companyLogoPreviewUrl ? (
          <button
            aria-label="Replace organization logo"
            className="absolute bottom-3 right-3 flex size-10 cursor-pointer items-center justify-center rounded-xl bg-button-primary text-white shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              openFilePicker();
            }}
          >
            <EditIcon className="size-4" />
          </button>
        ) : null}
      </div>
      <input
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        ref={logoFileInputRef}
        onChange={handleFileChange}
        type="file"
      />
      <div className="flex flex-col gap-1">
        {showRemoveAction && (
          <Button
            className="h-auto justify-start p-0 text-sm font-medium text-gray-500 hover:text-red-600 hover:no-underline"
            type="button"
            variant="link"
            onClick={() => onCompanyLogoRemove?.()}
          >
            Remove logo
          </Button>
        )}
        {showRestoreAction && (
          <Button
            className="h-auto justify-start p-0 text-sm font-medium text-gray-500 hover:text-blue-600 hover:no-underline"
            type="button"
            variant="link"
            onClick={() => onCompanyLogoRestore?.()}
          >
            Undo remove
          </Button>
        )}
      </div>
      {companyLogoError && <p className="text-xs text-red-600">{companyLogoError}</p>}
    </div>
  );
};
